import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Cone, Circle, Goal, Shirt, ClipboardList, Package } from 'lucide-react';
import {
  SessionSetup,
  SessionPlan,
  SessionFocus,
  TimeBlock,
  DrillGroup,
  Dimension,
  AgeGroup,
  TrainingDrill,
  SaveTrainingSessionRequest,
  UpdateTrainingSessionRequest,
  SessionEnd,
} from '../Model';
import { DrillSwapModal } from './DrillSwapModal';
import { formatTimeRange } from '../utils';
import { restClient } from '../RestClient';
import { YouTubeEmbed, getYouTubeVideoId } from '../YouTubeEmbed';
import { useReferenceData } from '../ReferenceDataContext';
import { calculateEquipmentSummary } from './equipmentUtils';

// Input Form Component
interface SessionInputFormProps {
  onGenerate: (setup: SessionSetup) => void;
  isGenerating?: boolean;
}

const SessionInputForm: React.FC<SessionInputFormProps> = ({ onGenerate, isGenerating }) => {
  const { referenceData, currentSport, currentSportConfig } = useReferenceData();
  const [coaches, setCoaches] = useState(2);
  const [players, setPlayers] = useState(16);
  const [pitchSize, setPitchSize] = useState<string>('full');
  const [totalTime, setTotalTime] = useState(90);
  const [sessionFocus, setSessionFocus] = useState<SessionFocus>('Passing & Movement');
  const [ageGroup, setAgeGroup] = useState<AgeGroup>('Youth');
  const [sessionEnd, setSessionEnd] = useState<SessionEnd>('Match');
  const [mainDrillBlocks, setMainDrillBlocks] = useState(2);
  const [minGroupSize, setMinGroupSize] = useState(8);

  // Reset session focus when sport changes
  React.useEffect(() => {
    if (currentSportConfig?.sessionFocusOptions.length) {
      setSessionFocus(currentSportConfig.sessionFocusOptions[0] as SessionFocus);
    }
  }, [currentSport, currentSportConfig]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pitchPresets = referenceData?.pitchPresets;
    if (!pitchPresets) return;
    const preset = pitchPresets[pitchSize as keyof typeof pitchPresets];
    const setup: SessionSetup = {
      coaches,
      players,
      pitchDimensions: { width: preset.width, height: preset.height },
      totalTime,
      sessionFocus,
      ageGroup,
      structure: {
        sessionEnd,
        mainDrillBlocks,
      },
      sport: currentSport,
      minGroupSize,
    };
    onGenerate(setup);
  };

  return (
    <div className="session-input-form">
      <h3 className="session-input-form-title">Session Parameters</h3>

      <form onSubmit={handleSubmit}>
        <div className="session-input-grid">
          <div className="session-input-row">
            <div className="form-group">
              <label className="form-label">Coaches</label>
              <input
                type="number"
                min={1}
                max={10}
                value={coaches}
                onChange={(e) => setCoaches(parseInt(e.target.value) || 1)}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Players</label>
              <input
                type="number"
                min={4}
                max={40}
                value={players}
                onChange={(e) => setPlayers(parseInt(e.target.value) || 4)}
                className="form-control"
              />
            </div>
          </div>

          <div className="session-input-row">
            <div className="form-group">
              <label className="form-label">Min Group Size</label>
              <select
                value={minGroupSize}
                onChange={(e) => setMinGroupSize(parseInt(e.target.value))}
                className="form-select"
              >
                <option value={4}>4 players</option>
                <option value={6}>6 players</option>
                <option value={8}>8 players</option>
                <option value={10}>10 players</option>
                <option value={12}>12 players</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Pitch Size</label>
              <select
                value={pitchSize}
                onChange={(e) => setPitchSize(e.target.value)}
                className="form-select"
              >
                {Object.entries(referenceData?.pitchPresets ?? {}).map(([key, preset]) => (
                  <option key={key} value={key}>
                    {preset.name} ({preset.width}x{preset.height}m)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="session-input-row">
            <div className="form-group">
              <label className="form-label">Duration (minutes)</label>
              <input
                type="number"
                min={30}
                max={180}
                step={15}
                value={totalTime}
                onChange={(e) => setTotalTime(parseInt(e.target.value) || 60)}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Session Focus</label>
              <select
                value={sessionFocus}
                onChange={(e) => setSessionFocus(e.target.value as SessionFocus)}
                className="form-select"
              >
                {(currentSportConfig?.sessionFocusOptions ?? referenceData?.sessionFocusOptions ?? []).map((focus) => (
                  <option key={focus} value={focus}>{focus}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="session-input-row">
            <div className="form-group">
              <label className="form-label">Age Group</label>
              <select
                value={ageGroup}
                onChange={(e) => setAgeGroup(e.target.value as AgeGroup)}
                className="form-select"
              >
                {(referenceData?.ageGroups ?? []).map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">End With</label>
              <select
                value={sessionEnd}
                onChange={(e) => setSessionEnd(e.target.value as SessionEnd)}
                className="form-select"
              >
                <option value="Match">Match</option>
                <option value="Cooldown">Cool-down & Debrief</option>
              </select>
            </div>
          </div>

          <div className="session-input-row">
            <div className="form-group">
              <label className="form-label">Main Drill Blocks</label>
              <select
                value={mainDrillBlocks}
                onChange={(e) => setMainDrillBlocks(parseInt(e.target.value))}
                className="form-select"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={isGenerating}
            className="btn-generate"
          >
            {isGenerating ? 'Generating...' : 'Generate Session Plan'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Pitch Layout Diagram Component
interface PitchLayoutDiagramProps {
  pitchDimensions: Dimension;
  drillGroups: DrillGroup[];
}

const PitchLayoutDiagram: React.FC<PitchLayoutDiagramProps> = ({ pitchDimensions, drillGroups }) => {
  const viewWidth = 300;
  const viewHeight = (pitchDimensions.height / pitchDimensions.width) * viewWidth;
  const scaleX = viewWidth / pitchDimensions.width;
  const scaleY = viewHeight / pitchDimensions.height;

  const colors = ['#667eea', '#28a745', '#ffc107', '#dc3545', '#17a2b8'];

  const allStations = drillGroups.flatMap(g =>
    g.stations.map(s => ({ ...s, drillName: g.drill.name }))
  );

  return (
    <div className="pitch-layout-container">
      <h4 className="pitch-layout-title">Pitch Layout</h4>
      <svg
        viewBox={`0 0 ${viewWidth} ${viewHeight}`}
        role="img"
        aria-label={`Pitch layout showing ${allStations.length} group${allStations.length !== 1 ? 's' : ''}: ${drillGroups.map(g => g.drill.name).join(', ')}`}
        className="pitch-svg"
      >
        {/* Center circle */}
        <circle
          cx={viewWidth / 2}
          cy={viewHeight / 2}
          r={Math.min(viewWidth, viewHeight) * 0.15}
          fill="none"
          stroke="#28a745aa"
          strokeWidth={1.5}
        />
        {/* Center line */}
        <line
          x1={viewWidth / 2}
          y1={0}
          x2={viewWidth / 2}
          y2={viewHeight}
          stroke="#28a745aa"
          strokeWidth={1.5}
        />

        {/* Station areas */}
        {allStations.map((station, idx) => {
          const area = station.pitchArea;
          return (
            <g key={`${station.id}-${idx}`}>
              <rect
                x={area.x * scaleX}
                y={area.y * scaleY}
                width={area.width * scaleX}
                height={area.height * scaleY}
                fill={`${colors[idx % colors.length]}40`}
                stroke={colors[idx % colors.length]}
                strokeWidth={2}
                rx={4}
              />
              <text
                x={(area.x + area.width / 2) * scaleX}
                y={(area.y + area.height / 2) * scaleY}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={11}
                fontWeight={600}
                fill={colors[idx % colors.length]}
              >
                {station.assignedPlayers.groupName}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="pitch-legend">
        {allStations.map((station, idx) => (
          <div key={`${station.id}-${idx}`} className="pitch-legend-item">
            <div className="pitch-legend-color" style={{ backgroundColor: colors[idx % colors.length] }} />
            <span className="pitch-legend-label">{station.drillName} - {station.assignedPlayers.groupName}</span>
          </div>
        ))}
      </div>
    </div>
  );
};


// Drill Card Component
interface DrillCardProps {
  group: DrillGroup;
  showCoach: boolean;
  onSwapClick?: () => void;
}

const DrillCard: React.FC<DrillCardProps> = ({ group, showCoach, onSwapClick }) => {
  const [showVideo, setShowVideo] = useState(false);
  const drill = group.drill;
  const videoId = drill.youtubeUrl ? getYouTubeVideoId(drill.youtubeUrl) : null;

  return (
    <div className="station-card">
      <div className="station-header">
        <div className="station-name">{drill.name}</div>
      </div>

      {/* Station assignments */}
      <div className="station-groups">
        {group.stations.map((station) => (
          <div key={station.id} className="station-group-info">
            <span>{station.assignedPlayers.groupName} ({station.assignedPlayers.count} players)</span>
            {showCoach && station.assignedCoaches.length > 0 && (
              <span className="station-coach-badge">
                {station.assignedCoaches.length === 1
                  ? `Coach ${station.assignedCoaches[0]}`
                  : `Coaches ${station.assignedCoaches.join(', ')}`}
              </span>
            )}
            <span className="text-sm text-muted">
              {station.pitchArea.label}
            </span>
          </div>
        ))}
      </div>

      <div className="station-skills">
        {drill.skills.map((skill) => (
          <span key={skill} className="station-skill-badge">{skill}</span>
        ))}
      </div>

      {/* Swap Button */}
      {onSwapClick && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSwapClick();
          }}
          className="station-swap-btn"
          title="Swap this drill with a similar one"
          aria-label={`Swap drill ${drill.name}`}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 3l4 4-4 4" />
            <path d="M20 7H4" />
            <path d="M8 21l-4-4 4-4" />
            <path d="M4 17h16" />
          </svg>
          <span>Swap</span>
        </button>
      )}

      {/* Video Section */}
      {drill.youtubeUrl && (
        <div className="station-video-actions">
          {videoId && (
            <button
              onClick={() => setShowVideo(!showVideo)}
              className="station-video-primary"
            >
              {showVideo ? 'Hide Video' : '\u25B6 Watch Video'}
            </button>
          )}
          <a
            href={drill.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="station-video-link-subtle"
          >
            Open in YouTube
          </a>
        </div>
      )}

      {/* Embedded Video */}
      {showVideo && videoId && drill.youtubeUrl && (
        <div className="station-video-embed">
          <YouTubeEmbed
            url={drill.youtubeUrl}
            title={`${drill.name} - training video`}
            borderRadius={6}
          />
        </div>
      )}
    </div>
  );
};

// Time Block Display Component
interface TimeBlockDisplayProps {
  block: TimeBlock;
  coaches: number;
  pitchDimensions: Dimension;
  onDrillSwapClick?: (drillId: number, drill: TrainingDrill, timeBlockDuration: number) => void;
}

const TimeBlockDisplay: React.FC<TimeBlockDisplayProps> = ({ block, coaches, pitchDimensions, onDrillSwapClick }) => {
  const getHeaderClass = (blockName: string) => {
    if (blockName === 'Warm-up') return 'time-block-header time-block-header-warmup';
    if (blockName.includes('Main')) return 'time-block-header time-block-header-main';
    if (blockName.includes('Game') || blockName.includes('Match')) return 'time-block-header time-block-header-game';
    if (blockName.includes('Cool-down')) return 'time-block-header time-block-header-cooldown';
    return 'time-block-header';
  };

  const totalStations = block.drillGroups.reduce((sum, g) => sum + g.stations.length, 0);

  return (
    <div className="time-block">
      <div className={getHeaderClass(block.name)}>
        <div className="time-block-name">{block.name}</div>
        <div className="time-block-time">{formatTimeRange(block.startTime, block.duration)}</div>
      </div>

      <div className="time-block-body">
        <PitchLayoutDiagram
          pitchDimensions={pitchDimensions}
          drillGroups={block.drillGroups}
        />

        {block.drillGroups.map((group, idx) => (
          <DrillCard
            key={`${group.drill.id}-${idx}`}
            group={group}
            showCoach={totalStations > 1 && coaches > 1}
            onSwapClick={onDrillSwapClick ? () => onDrillSwapClick(group.drill.id, group.drill, block.duration) : undefined}
          />
        ))}

        {block.rotations && block.rotations.length > 0 && (
          <div className="rotation-schedule">
            <div className="rotation-schedule-title">Rotation Schedule:</div>
            {block.rotations.map((rotation, idx) => (
              <div key={idx} className="rotation-schedule-item">
                At {rotation.atMinute} min: {rotation.description}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Equipment Summary Component
interface EquipmentSummaryProps {
  equipment: SessionPlan['equipmentSummary'];
}

const EQUIPMENT_ICONS: Record<string, React.FC<{ size?: number; className?: string }>> = {
  'Cones': Cone,
  'Footballs': Circle,
  'Rugby Balls': Circle,
  'Goals': Goal,
  'Bibs': Shirt,
};

const EquipmentSummaryDisplay: React.FC<EquipmentSummaryProps> = ({ equipment }) => {
  const items = Object.entries(equipment)
    .filter(([, qty]) => qty > 0)
    .map(([name, qty]) => ({
      label: name,
      value: qty,
      icon: EQUIPMENT_ICONS[name] ?? Package,
    }));

  return (
    <div className="equipment-summary">
      <h4 className="equipment-summary-title">Equipment Required</h4>
      <div className="equipment-summary-grid">
        {items.map((item) => {
          const IconComponent = item.icon;
          return (
            <div key={item.label} className="equipment-item">
              <IconComponent size={18} className="equipment-icon" />
              <span className="equipment-label">{item.value} {item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Session Plan Display Component
interface SessionPlanDisplayProps {
  plan: SessionPlan;
  onDrillSwapClick?: (timeBlockId: number, drillId: number, drill: TrainingDrill, timeBlockDuration: number) => void;
}

const SessionPlanDisplay: React.FC<SessionPlanDisplayProps> = ({ plan, onDrillSwapClick }) => {
  const { setup, timeBlocks, equipmentSummary } = plan;

  return (
    <div>
      {/* Session Overview */}
      <div className="page-header">
        <h3 className="page-title">{setup.sessionFocus} Session</h3>
        <div className="session-overview">
          <div className="session-stat-card">
            <div className="session-stat-value session-stat-value-primary">{setup.totalTime}</div>
            <div className="session-stat-label">minutes</div>
          </div>
          <div className="session-stat-card">
            <div className="session-stat-value session-stat-value-success">{setup.players}</div>
            <div className="session-stat-label">players</div>
          </div>
          <div className="session-stat-card">
            <div className="session-stat-value session-stat-value-warning">{setup.coaches}</div>
            <div className="session-stat-label">coaches</div>
          </div>
          <div className="session-stat-card">
            <div className="session-stat-value-text">
              {setup.pitchDimensions.width}x{setup.pitchDimensions.height}m
            </div>
            <div className="session-stat-label">pitch size</div>
          </div>
        </div>
      </div>

      {/* Equipment Summary */}
      <EquipmentSummaryDisplay equipment={equipmentSummary} />

      {/* Time Blocks */}
      <h4 className="session-timeline-title">Session Timeline</h4>
      {timeBlocks.map((block) => (
        <TimeBlockDisplay
          key={block.id}
          block={block}
          coaches={setup.coaches}
          pitchDimensions={setup.pitchDimensions}
          onDrillSwapClick={onDrillSwapClick ? (drillId, drill, timeBlockDuration) => onDrillSwapClick(block.id, drillId, drill, timeBlockDuration) : undefined}
        />
      ))}
    </div>
  );
};

// Main Training Planner Component
interface TrainingPlannerProps {
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

// Helper to safely parse session ID from URL
const parseSessionId = (id: string | undefined): number | null => {
  if (!id) return null;
  const parsed = parseInt(id, 10);
  return isNaN(parsed) ? null : parsed;
};

export function TrainingPlanner({ onError, onSuccess }: TrainingPlannerProps) {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();

  const [sessionPlan, setSessionPlan] = useState<SessionPlan | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(!!sessionId);
  const [isSaving, setIsSaving] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedSessionId, setSavedSessionId] = useState<number | null>(
    parseSessionId(sessionId)
  );
  const [savedSessionVersion, setSavedSessionVersion] = useState<number>(0);
  const [swapModal, setSwapModal] = useState<{
    timeBlockId: number;
    drillId: number;
    drill: TrainingDrill;
    timeBlockDuration: number;
  } | null>(null);

  // Use ref to avoid re-fetching when onError callback changes
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Load session data when editing
  useEffect(() => {
    if (!sessionId) return;

    const parsedId = parseSessionId(sessionId);
    if (parsedId === null) {
      onErrorRef.current?.('Invalid session ID');
      navigate('/training-sessions');
      setIsLoadingSession(false);
      return;
    }

    const loadSession = async () => {
      setIsLoadingSession(true);
      const result = await restClient.getTrainingSession(parsedId);
      if (result.IsSuccess) {
        // Check if user has permission to edit
        if (!result.Value.isEditable) {
          onErrorRef.current?.('You do not have permission to edit this session.');
          navigate('/training-sessions');
          setIsLoadingSession(false);
          return;
        }

        const session = result.Value.session;
        // Convert TrainingSession to SessionPlan format
        const plan: SessionPlan = {
          setup: {
            coaches: session.coaches,
            players: session.players,
            pitchDimensions: session.pitchDimensions,
            totalTime: session.totalTime,
            sessionFocus: session.sessionFocus as SessionFocus,
            ageGroup: session.ageGroup as AgeGroup,
          },
          timeBlocks: session.timeBlocks,
          equipmentSummary: session.equipmentSummary,
        };
        setSessionPlan(plan);
        setSessionName(session.name);
        setSavedSessionId(session.id);
        setSavedSessionVersion(session.version);
      } else {
        onErrorRef.current?.(result.Error?.description ?? 'Failed to load session for editing.');
        navigate('/training-sessions');
      }
      setIsLoadingSession(false);
    };

    loadSession();
  }, [sessionId, navigate]);

  const handleGenerate = async (setup: SessionSetup) => {
    setIsGenerating(true);
    const result = await restClient.generateSessionPlan(setup);
    if (result.IsSuccess) {
      setSessionPlan(result.Value);
      // Pre-populate session name
      const defaultName = `${setup.sessionFocus} - ${setup.totalTime}min - ${new Date().toISOString().split('T')[0]}`;
      setSessionName(defaultName);
      // Reset saved state for new session
      setSavedSessionId(null);
      setSavedSessionVersion(0);
    } else {
      console.error('Error generating session plan:', result.Error);
      onError?.(result.Error?.description ?? 'Failed to generate session plan. Please try again.');
    }
    setIsGenerating(false);
  };

  const handleSave = async () => {
    if (!sessionPlan) return;

    setIsSaving(true);

    if (savedSessionId !== null) {
      // Update existing session
      const request: UpdateTrainingSessionRequest = {
        id: savedSessionId,
        version: savedSessionVersion,
        name: sessionName.trim() || undefined,
        sessionPlan,
      };

      const result = await restClient.updateTrainingSession(request);
      if (result.IsSuccess) {
        setSavedSessionVersion(result.Value.version);
        onSuccess?.(`Session "${result.Value.name}" updated successfully!`);
        setShowSaveModal(false);
      } else {
        onError?.(result.Error.description ?? 'Failed to update session. Please try again.');
      }
    } else {
      // Save new session
      const request: SaveTrainingSessionRequest = {
        name: sessionName.trim() || undefined,
        sessionPlan,
      };

      const result = await restClient.saveTrainingSession(request);
      if (result.IsSuccess) {
        setSavedSessionId(result.Value.id);
        setSavedSessionVersion(result.Value.version);
        onSuccess?.(`Session "${result.Value.name}" saved successfully!`);
        setShowSaveModal(false);
      } else {
        onError?.(result.Error.description ?? 'Failed to save session. Please try again.');
      }
    }
    setIsSaving(false);
  };

  const handleOpenSwapModal = (timeBlockId: number, drillId: number, drill: TrainingDrill, timeBlockDuration: number) => {
    setSwapModal({ timeBlockId, drillId, drill, timeBlockDuration });
  };


  const handleDrillSwap = (newDrill: TrainingDrill) => {
    if (!sessionPlan || !swapModal) return;

    // Update the session plan - replace the drill on the entire DrillGroup
    const updatedTimeBlocks = sessionPlan.timeBlocks.map((block) => {
      if (block.id !== swapModal.timeBlockId) return block;

      return {
        ...block,
        drillGroups: block.drillGroups.map((group) => {
          if (group.drill.id !== swapModal.drillId) return group;

          return {
            ...group,
            drill: newDrill,
          };
        }),
      };
    });

    // Recalculate equipment summary
    const newEquipmentSummary = calculateEquipmentSummary(updatedTimeBlocks);

    setSessionPlan({
      ...sessionPlan,
      timeBlocks: updatedTimeBlocks,
      equipmentSummary: newEquipmentSummary,
    });

    setSwapModal(null);
    onSuccess?.(`Swapped "${swapModal.drill.name}" with "${newDrill.name}"`);
  };

  const isEditMode = savedSessionId !== null;

  if (isLoadingSession) {
    return (
      <div className="loading-container">
        <p className="loading-text">Loading session...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h2 className="page-title">
          {isEditMode ? 'Edit Training Session' : 'Training Planner'}
        </h2>
        <p className="page-description">
          {isEditMode
            ? 'Modify your training session and save your changes.'
            : 'Generate complete training session plans automatically. Set your parameters and let the planner create an optimized session with drills, rotations, and pitch layouts.'}
        </p>
      </div>

      {/* Main Content - Side by side on larger screens */}
      <div className={`planner-grid ${sessionPlan ? 'planner-grid-with-plan' : ''}`}>
        {/* Input Form */}
        <SessionInputForm onGenerate={handleGenerate} isGenerating={isGenerating} />

        {/* Generated Plan */}
        {sessionPlan && (
          <div>
            <SessionPlanDisplay plan={sessionPlan} onDrillSwapClick={handleOpenSwapModal} />
            {/* Save/Update Button */}
            <div className="planner-save-container">
              <button
                onClick={() => setShowSaveModal(true)}
                className={`btn-save-session ${savedSessionId !== null ? 'btn-save-session-update' : 'btn-save-session-new'}`}
              >
                {savedSessionId !== null ? 'Update Session' : 'Save Session'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="save-modal-content">
            <h3 className="save-modal-title">
              {savedSessionId !== null ? 'Update Training Session' : 'Save Training Session'}
            </h3>
            <div className="save-modal-field">
              <label htmlFor="session-name-input" className="form-label">Session Name</label>
              <input
                id="session-name-input"
                type="text"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                placeholder="Enter session name"
                className="form-control"
              />
              <p className="save-modal-hint">Leave empty to use the auto-generated name</p>
            </div>
            <div className="save-modal-actions">
              <button
                onClick={() => setShowSaveModal(false)}
                disabled={isSaving}
                className="btn btn-secondary"
                style={{ opacity: isSaving ? 0.7 : 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`btn ${savedSessionId !== null ? 'btn-primary' : 'btn-success'}`}
                style={{ opacity: isSaving ? 0.7 : 1 }}
              >
                {isSaving ? (savedSessionId !== null ? 'Updating...' : 'Saving...') : (savedSessionId !== null ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state when no plan generated */}
      {!sessionPlan && (
        <div className="empty-state mt-6">
          <div className="empty-state-icon-gradient">
            <ClipboardList size={32} />
          </div>
          <p className="empty-state-text">
            Configure your session parameters and click "Generate Session Plan" to create your training plan.
          </p>
        </div>
      )}

      {/* Drill Swap Modal */}
      {swapModal && sessionPlan && (
        <DrillSwapModal
          currentDrill={swapModal.drill}
          timeBlockDuration={swapModal.timeBlockDuration}
          sessionAgeGroup={sessionPlan.setup.ageGroup}
          onSwap={handleDrillSwap}
          onClose={() => setSwapModal(null)}
        />
      )}
    </div>
  );
}
